import { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { Medal, Trophy, X } from 'lucide-react';
import { db } from '../../../firebase/config';
import { Card, CardContent } from '../../ui/card';
import { cn } from '../../../lib/utils';

const medalClass = {
  1: 'text-gold',
  2: 'text-silver',
  3: 'text-bronze',
};

const Placement = ({ placement }) => {
  if (!placement || placement === '-') return <span className="text-muted-foreground/40">—</span>;
  const top = placement <= 3;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-mono tabular-nums',
        top ? `${medalClass[placement]} font-semibold` : 'text-foreground/80'
      )}
    >
      {top && <Medal className="h-3.5 w-3.5" />}
      {placement}
    </span>
  );
};

const sortEliminated = (players, maps) => {
  if (!players || !maps) return [];
  return [...players].sort((a, b) => {
    const lastIndex = (p) => {
      for (let i = maps.length - 1; i >= 0; i--) {
        if (maps[i].rankings?.some((r) => r.playerId === p.id)) return i;
      }
      return -1;
    };
    return lastIndex(b) - lastIndex(a);
  });
};

const StageTwo = ({ tournamentId, mode }) => {
  const [knockoutData, setKnockoutData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setKnockoutData(null);
      try {
        const snap = await getDocs(
          query(collection(db, 'tournaments', tournamentId, 'knockout'), where('mode', '==', mode))
        );
        if (cancelled) return;
        if (!snap.empty) {
          const data = snap.docs[0].data();
          if (data.mode === mode) setKnockoutData(data);
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [tournamentId, mode]);

  if (loading) {
    return <div className="h-48 animate-pulse rounded-md border border-border/60 bg-muted/10" />;
  }

  if (!knockoutData || !knockoutData.players?.length) {
    return (
      <div className="border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
        Knockout stage hasn't started yet.
      </div>
    );
  }

  const eliminated = knockoutData.eliminatedPlayers || [];
  const active = (knockoutData.players || []).filter(
    (p) => !eliminated.some((ep) => ep.id === p.id)
  );
  const maps = knockoutData.maps || [];
  const isWinnerCrowned = active.length === 1;

  return (
    <Card className="overflow-hidden border-border/60 bg-background/40">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60">
                <th className="w-20 px-3 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground">
                  Rank
                </th>
                <th className="px-3 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground">
                  Player
                </th>
                {maps.map((map) => (
                  <th
                    key={map.id}
                    className="min-w-[90px] px-3 py-2.5 text-center font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground"
                  >
                    {map.name}
                  </th>
                ))}
                <th className="w-28 px-3 py-2.5 text-center font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {active.map((player, index) => {
                const winner = isWinnerCrowned;
                return (
                  <tr
                    key={player.id}
                    className={cn(
                      'border-b border-border/40 last:border-0',
                      winner && 'bg-gold/10'
                    )}
                  >
                    <td
                      className={cn(
                        'px-3 py-2.5 font-mono text-sm tabular-nums',
                        winner ? 'text-gold font-semibold' : 'text-foreground/80'
                      )}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </td>
                    <td
                      className={cn(
                        'px-3 py-2.5 font-display text-sm',
                        winner ? 'text-gold font-semibold' : 'font-medium'
                      )}
                    >
                      {player.name}
                    </td>
                    {maps.map((map) => {
                      const placement = map.rankings?.find(
                        (r) => r.playerId === player.id
                      )?.placement;
                      return (
                        <td key={map.id} className="px-3 py-2.5 text-center">
                          <Placement placement={placement} />
                        </td>
                      );
                    })}
                    <td className="px-3 py-2.5 text-center">
                      {winner ? (
                        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
                          <Trophy className="h-3.5 w-3.5" />
                          Winner
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-success">
                          <span className="h-1.5 w-1.5 rounded-full bg-success" />
                          Active
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}

              {sortEliminated(eliminated, maps).map((player) => (
                <tr key={player.id} className="border-b border-border/40 opacity-60 last:border-0">
                  <td className="px-3 py-2.5 font-mono text-sm text-muted-foreground tabular-nums">
                    —
                  </td>
                  <td className="px-3 py-2.5 font-display text-sm text-muted-foreground">
                    {player.name}
                  </td>
                  {maps.map((map) => {
                    const placement = map.rankings?.find(
                      (r) => r.playerId === player.id
                    )?.placement;
                    return (
                      <td key={map.id} className="px-3 py-2.5 text-center">
                        <Placement placement={placement} />
                      </td>
                    );
                  })}
                  <td className="px-3 py-2.5 text-center">
                    <span className="inline-flex items-center gap-1 font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-destructive">
                      <X className="h-3 w-3" />
                      Eliminated
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default StageTwo;
